export interface ManualEntryRouteContext {
  countyId?: string;
  countyName?: string;
  stateAbbr?: string;
}

const COUNTY_ID_PATTERN = /^([a-z0-9_]+)_([a-z]{2})$/i;
const STATE_ZIP_PATTERN = /,\s*([A-Z]{2})\s+\d{5}(?:-\d{4})?\s*$/i;

function normalizeCountyId(value: string): string {
  return value.trim().toLowerCase().replace(/-/g, "_");
}

function countyNameFromId(countyId: string): string | undefined {
  const match = countyId.match(COUNTY_ID_PATTERN);
  if (!match) return undefined;
  const countySlug = match[1];
  if (!countySlug || countySlug === "unknown") return undefined;
  return countySlug
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function countyIdFromName(countyName: string, stateAbbr: string): string {
  const countySlug = countyName
    .replace(/\s+County$/i, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${countySlug || "unknown"}_${stateAbbr.toLowerCase()}`;
}

export function resolveManualEntryRouteContext(
  searchParams: URLSearchParams,
): ManualEntryRouteContext {
  const address = searchParams.get("address") ?? "";
  const rawCountyId = searchParams.get("county_id")?.trim() ?? "";
  const rawCounty = searchParams.get("county")?.trim() ?? "";
  const countyParamIsId = COUNTY_ID_PATTERN.test(rawCounty);
  const countyIdFromParam = rawCountyId || (countyParamIsId ? rawCounty : "");
  const normalizedCountyId = countyIdFromParam
    ? normalizeCountyId(countyIdFromParam)
    : "";
  const stateFromId = normalizedCountyId.match(COUNTY_ID_PATTERN)?.[2]?.toUpperCase();
  const stateFromAddress = address.match(STATE_ZIP_PATTERN)?.[1]?.toUpperCase();
  const stateAbbr =
    searchParams.get("state")?.trim().toUpperCase() || stateFromId || stateFromAddress;
  const countyName = countyParamIsId
    ? countyNameFromId(normalizedCountyId)
    : rawCounty.replace(/\s+County$/i, "").trim() || undefined;
  const countyId = normalizedCountyId || (stateAbbr
    ? countyName
      ? countyIdFromName(countyName, stateAbbr)
      : `unknown_${stateAbbr.toLowerCase()}`
    : undefined);

  return { countyId, countyName, stateAbbr };
}
